import { useState } from 'react';
import { Alert, Button, FormControl, FormLabel, Input, Option, Select, Stack, Typography } from '@mui/joy';
import { useAuth } from '../../contexts/AuthContext';
import { ordersApi, type Order } from '../../lib/distributorsApi';
import { extractApiError } from '../../utils/apiError';

export default function SubmissionResolution({ order, onResolved }: { order: Order; onResolved: () => void }) {
  const { user } = useAuth();
  const [resolution, setResolution] = useState<'accepted' | 'not_placed'>('accepted');
  const [reference, setReference] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  if (!order.api_submission_state) return null;
  const save = async () => {
    setSaving(true); setError('');
    try {
      await ordersApi.resolveSubmission(order.id, { resolution, reference, confirmation_number: confirmation });
      onResolved();
    } catch (err) { setError(extractApiError(err, 'Could not reconcile this order.')); }
    finally { setSaving(false); }
  };
  return <Alert color="warning" sx={{ alignItems: 'flex-start' }}>
    <Stack spacing={1.5}>
      <Typography level="title-md">Check with the branch before ordering again</Typography>
      <Typography level="body-sm">This order may already have been received. Editing, resubmitting, duplicating and cancelling are blocked until an administrator records the branch’s response. A submission still processing must wait five minutes before reconciliation.</Typography>
      {(user?.is_organization_admin || user?.is_superuser) && <>
        <FormControl><FormLabel>Branch confirmed</FormLabel>
          <Select value={resolution} onChange={(_, value) => value && setResolution(value)}>
            <Option value="accepted">Order was received</Option>
            <Option value="not_placed">Order was not placed — allow another attempt</Option>
          </Select>
        </FormControl>
        {resolution === 'accepted' && <FormControl required><FormLabel>Distributor confirmation number</FormLabel>
          <Input value={confirmation} onChange={event => setConfirmation(event.target.value)} slotProps={{ input: { maxLength: 64 } }} />
        </FormControl>}
        <FormControl required><FormLabel>Who confirmed this and when?</FormLabel>
          <Input value={reference} onChange={event => setReference(event.target.value)} placeholder="Branch contact, date and reference" slotProps={{ input: { maxLength: 500 } }} />
        </FormControl>
        {error && <Typography color="danger">{error}</Typography>}
        <Button color="warning" loading={saving} disabled={!reference.trim() || (resolution === 'accepted' && !confirmation.trim())} onClick={save}>Record branch confirmation</Button>
      </>}
    </Stack>
  </Alert>;
}
